import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { MedicalEquipment } from '@services/api'
import {
  createMedicalEquipment,
  findAllMedicalEquipment,
  updateMedicalEquipment,
} from '@state/thunks/medicalEquipmentThunk'

interface MedicalEquipmentState {
  data: MedicalEquipment[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
  filters: {
    search: string
    type: string
  }
  loading: boolean
  error: string | null
}

const initialState: MedicalEquipmentState = {
  data: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    type: '',
  },
  loading: false,
  error: null,
}

const medicalEquipmentSlice = createSlice({
  name: 'medical-equipment',
  initialState,
  reducers: {
    setMedicalPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload
    },
    setMedicalLimit: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload
      state.pagination.page = 1
    },

    setMedicalSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload
      state.pagination.page = 1
    },
    setMedicalTypeFilter: (state, action: PayloadAction<string>) => {
      state.filters.type = action.payload
      state.pagination.page = 1
    },
    clearMedicalFilters: (state) => {
      state.filters = { search: '', type: '' }
      state.pagination.page = 1
    },

    clearMedicalEquipments: (state) => {
      state.data = []
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      }
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(findAllMedicalEquipment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        findAllMedicalEquipment.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          state.loading = false
          state.data = payload.data
          state.pagination = payload.meta
        },
      )
      .addCase(findAllMedicalEquipment.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.error.message || 'Failed to fetch medical equipments'
      })
      .addCase(createMedicalEquipment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        createMedicalEquipment.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          state.loading = false
          state.data = [payload, ...state.data]
          state.pagination.total = state.pagination.total + 1
        },
      )
      .addCase(createMedicalEquipment.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.error.message || 'Failed to create medical equipment'
      })
      .addCase(updateMedicalEquipment.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        updateMedicalEquipment.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          state.loading = false
          const index = state.data.findIndex((_eq) => _eq.id === payload.id)
          if (index !== -1) {
            state.data[index] = payload
          }
        },
      )
      .addCase(updateMedicalEquipment.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.error.message || 'Failed to update medical equipment'
      }),
})

export const {
  setMedicalPage,
  setMedicalLimit,
  setMedicalSearch,
  setMedicalTypeFilter,
  clearMedicalFilters,
  clearMedicalEquipments,
} = medicalEquipmentSlice.actions

export default medicalEquipmentSlice.reducer
